import { BoxGeometry, ExtrudeGeometry, Mesh, MeshNormalMaterial, MeshStandardMaterial } from 'three';

import type { CutoutInfo } from '../types';
import { cutoutProfileShapes } from '../helpers/cutout-profiles.helpers';

export interface CreateCutoutMeshParams {
	cutout: CutoutInfo;
}

/**
 * Create a cutout mesh from cutout info (matching legacy behavior)
 */
export const createCutoutMeshCfn = (
	{ cutout }: CreateCutoutMeshParams
): Mesh => {
	// A vent cuts several separate openings through one panel (the slots of its 2D artwork).
	// Those extrude as one multi-shape geometry so the whole vent costs a single CSG subtract.
	const shapes = cutoutProfileShapes(cutout);

	if (shapes.length) {
		// Extrude settings
		const extrudeSettings = {
			depth: cutout.depth,
			bevelEnabled: false
		};

		const geometry = new ExtrudeGeometry(shapes, extrudeSettings);
		geometry.computeVertexNormals();
		geometry.center();

		geometry.clearGroups();
		geometry.addGroup(0, geometry.index ? geometry.index.count : geometry.attributes.position.count, 0);

		const material = new MeshNormalMaterial();
		const mesh = new Mesh(geometry, material);

		// Default: `points` is a horizontal footprint, so the extrusion is tipped upright.
		// A vertical profile (arch) is already in the vertical plane and extrudes straight
		// back into the panel, so it must NOT be tipped -- only swung to the panel angle.
		if (cutout.verticalProfile) {
			mesh.rotation.x = 0;
			mesh.rotation.y = cutout.rotation?.y ?? 0;
		} else {
			// Apply rotation matching legacy behavior:
			// hole.rotation.x = Math.PI / 2 (90 degrees)
			// hole.rotation.z = cutout.rotationY
			mesh.rotation.x = Math.PI / 2;
			if (cutout.rotation?.y) {
				mesh.rotation.z = cutout.rotation.y;
			}
		}

		// Apply position matching legacy behavior:
		// hole.position.x = cutout.x
		// hole.position.y = cutout.y * -1
		// hole.position.z = cutout.z * -1
		const finalPosition = {
			x: cutout.position.x,
			y: cutout.position.y * -1,
			z: cutout.position.z * -1
		};
		mesh.position.set(finalPosition.x, finalPosition.y, finalPosition.z);

		mesh.updateMatrix();

		return mesh;
	}
	// Default to box geometry for simple cutouts
	const geometry = new BoxGeometry(20, cutout.depth, 16);

	geometry.clearGroups();
	geometry.addGroup(0, geometry.index ? geometry.index.count : geometry.attributes.position.count, 0);

	const material = new MeshStandardMaterial({ visible: false });
	const mesh = new Mesh(geometry, material);

	mesh.rotation.x = Math.PI / 2;
	mesh.position.set(
		cutout.position.x,
		cutout.position.y * -1,
		cutout.position.z * -1
	);
	mesh.updateMatrix();

	return mesh;
};
